// src/components/logistique/apiLogistique.js
// Appels de l'administration vers /api/logistique/parc (jeton employé).
//
// Même lecture des refus que apiSuiviEnseignants.js : le refus de licence n'a
// pas de `message` français, une table absente dit que le module n'est pas
// installé, le reste s'affiche tel que le serveur l'a écrit (un refus du
// serveur s'affiche toujours, LICENCES-CATALOGUE.md § 5).

import { getAuthHeaders } from '../../services/api';

const RACINE = '/api/logistique/parc';

const LICENCE_FERMEE = "La logistique n'est pas incluse dans la licence de cet établissement.";
const NON_INSTALLE = "Le registre du parc n'est pas encore installé dans cet établissement.";

function erreurDuServeur(reponse, donnees, defaut) {
  let message = donnees?.message;
  if (!message && donnees?.code === 'FEATURE_NOT_LICENSED') message = LICENCE_FERMEE;
  if (!message && (reponse.status === 503 || (reponse.status === 404 && !donnees))) message = NON_INSTALLE;
  const erreur = new Error(message || donnees?.error || defaut);
  erreur.code = donnees?.code;
  erreur.statut = reponse.status;
  return erreur;
}

/** Les en-têtes de l'administration, sans le Content-Type (FormData, lecture de fichier). */
function entetesSansType() {
  const entetes = { ...getAuthHeaders() };
  delete entetes['Content-Type'];
  return entetes;
}

async function appeler(chemin, { methode = 'GET', corps, formulaire } = {}) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: methode,
      headers: formulaire ? entetesSansType() : getAuthHeaders(),
      body: formulaire || (corps !== undefined ? JSON.stringify(corps) : undefined)
    });
  } catch {
    throw new Error('Connexion au serveur impossible. Vérifiez la connexion et réessayez.');
  }
  let donnees = null;
  try { donnees = await reponse.json(); } catch { donnees = null; }
  if (!reponse.ok) throw erreurDuServeur(reponse, donnees, 'Une erreur est survenue.');
  return donnees?.data;
}

/** Un fichier (PDF, photo) : la réponse brute, ou l'erreur JSON du serveur. */
async function telecharger(chemin, { methode = 'GET', corps } = {}) {
  let reponse;
  try {
    reponse = await fetch(`${RACINE}${chemin}`, {
      method: methode,
      headers: corps !== undefined ? getAuthHeaders() : entetesSansType(),
      body: corps !== undefined ? JSON.stringify(corps) : undefined
    });
  } catch {
    throw new Error('Connexion au serveur impossible. Vérifiez la connexion et réessayez.');
  }
  if (!reponse.ok) {
    let donnees = null;
    try { donnees = await reponse.json(); } catch { donnees = null; }
    throw erreurDuServeur(reponse, donnees, 'Le fichier n\'a pas pu être obtenu.');
  }
  return reponse.blob();
}

const enRequete = (filtres = {}) => {
  const p = new URLSearchParams();
  Object.entries(filtres).forEach(([cle, valeur]) => {
    if (valeur !== undefined && valeur !== null && valeur !== '') p.set(cle, valeur);
  });
  const texte = p.toString();
  return texte ? `?${texte}` : '';
};

/** Un mouvement part en FormData : la photo, et les champs en texte. */
function formulaireMouvement(champs, photo) {
  const f = new FormData();
  Object.entries(champs).forEach(([cle, valeur]) => {
    if (valeur !== undefined && valeur !== null && valeur !== '') f.append(cle, String(valeur));
  });
  if (photo) f.append('photo', photo, photo.name || 'photo.jpg');
  return f;
}

export const parc = {
  referentiels: () => appeler('/referentiels'),
  personnes: () => appeler('/personnes'),
  creerLieu: (nom) => appeler('/lieux', { methode: 'POST', corps: { nom } }),
  creerCategorie: (nom) => appeler('/categories', { methode: 'POST', corps: { nom } }),
  // Paramétrage de la logistique : réglages du parc, catégories et lieux.
  parametrage: () => appeler('/parametrage'),
  enregistrerParametrage: (reglages) => appeler('/parametrage', { methode: 'PUT', corps: reglages }),
  modifierCategorie: (id, champs) => appeler(`/categories/${encodeURIComponent(id)}`, { methode: 'PUT', corps: champs }),
  deplacerCategorie: (id, sens) => appeler(`/categories/${encodeURIComponent(id)}/deplacer`, { methode: 'POST', corps: { sens } }),
  modifierLieu: (id, champs) => appeler(`/lieux/${encodeURIComponent(id)}`, { methode: 'PUT', corps: champs }),
  lister: (filtres) => appeler(`/actifs${enRequete(filtres)}`),
  parCode: (texte) => appeler(`/code/${encodeURIComponent(texte)}`),
  fiche: (id) => appeler(`/actifs/${encodeURIComponent(id)}`),
  creer: (champs) => appeler('/actifs', { methode: 'POST', corps: champs }),
  modifier: (id, champs) => appeler(`/actifs/${encodeURIComponent(id)}`, { methode: 'PUT', corps: champs }),
  mouvement: (id, champs, photo) => appeler(`/actifs/${encodeURIComponent(id)}/mouvements`, {
    methode: 'POST',
    formulaire: formulaireMouvement(champs, photo)
  }),
  remplacerEtiquette: (id, commentaire) => appeler(`/actifs/${encodeURIComponent(id)}/etiquette`, {
    methode: 'POST',
    corps: { commentaire }
  }),
  etiquettes: (ids, format, debut) => telecharger('/etiquettes', { methode: 'POST', corps: { ids, format, debut } }),
  photo: (photoId, mini = false) => telecharger(`/photos/${encodeURIComponent(photoId)}${mini ? '?taille=mini' : ''}`)
};

/** Les inventaires du parc (lot 2). */
export const inventaires = {
  etat: () => appeler('/inventaires'),
  reglages: (reglages) => appeler('/inventaires/reglages', { methode: 'PUT', corps: reglages }),
  ouvrir: () => appeler('/inventaires', { methode: 'POST', corps: {} }),
  detail: (id) => appeler(`/inventaires/${encodeURIComponent(id)}`),
  /** Un scan ; la photo part avec lui quand la campagne l'exige. */
  scanner: (id, champs, photo) => appeler(`/inventaires/${encodeURIComponent(id)}/scans`, {
    methode: 'POST',
    formulaire: formulaireMouvement(champs, photo)
  }),
  liste: (id, codes, lieuId) => appeler(`/inventaires/${encodeURIComponent(id)}/liste`, {
    methode: 'POST',
    corps: { codes, lieu_id: lieuId || null }
  }),
  justifier: (id, ligneId, motif, commentaire) => appeler(
    `/inventaires/${encodeURIComponent(id)}/lignes/${encodeURIComponent(ligneId)}/justification`,
    { methode: 'POST', corps: { motif, commentaire } }
  ),
  cloturer: (id, commentaire) => appeler(`/inventaires/${encodeURIComponent(id)}/cloture`, {
    methode: 'POST',
    corps: { commentaire }
  })
};

/** Fait télécharger un Blob au navigateur sous un nom donné. */
export function enregistrerFichier(blob, nom) {
  const adresse = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = adresse;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 30000);
}
